/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Hook to get the connected wallet address from Privy
 */
import { useWallets } from '@privy-io/react-auth';

export interface UseWalletAddressReturn {
  /**
   * The connected wallet address (checksummed), or null if not connected
   */
  address: string | null;

  /**
   * Whether a wallet is currently connected
   */
  isConnected: boolean;

  /**
   * Whether Privy is still loading/initializing
   */
  isLoading: boolean;
}

/**
 * Get the connected wallet address from Privy
 *
 * Returns the first connected wallet address, or null if no wallet is connected.
 * In Phase 1, we use connected wallets (not linked accounts) for read-only access.
 * Phase 2 will use linked accounts for authentication.
 */
export function useWalletAddress(): UseWalletAddressReturn {
  const { ready, wallets } = useWallets();

  // Still loading if Privy hasn't initialized yet
  const isLoading = !ready;

  // Get the first connected wallet (EVM wallet)
  const firstWallet = wallets[0];
  const address = firstWallet?.address ?? null;
  const isConnected = !!address;

  return {
    address,
    isConnected,
    isLoading,
  };
}
